import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import { DatePicker, Input, Button, Tag, Badge, Modal, Tooltip, Card, Statistic, Row, Col } from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
    DownloadOutlined,
    EyeOutlined,
    FilterOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getRequest } from "../../../Utils/AppApi";
import moment from "moment";

const { RangePicker } = DatePicker;

// API Constants
const DMT_REFUND_AUDIT_LIST = "/admin/dmt/refund-audit";

const DmtRefundAuditPage = ({ history }) => {
    const csvLink = useRef();
    const [isLoading, setLoading] = useState(false);
    const [getData, setData] = useState([]);
    const [getSummary, setSummary] = useState(null);
    const [getPagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });

    const [page, setPage] = useState(1);
    const [isLimit, setLimit] = useState(50);

    // Filters
    const [partnerId, setPartnerId] = useState("");
    const [onlyDuplicates, setOnlyDuplicates] = useState(true);
    // Date range mandatory – default to last 5 days (matches your audit window habit)
    const [dateRange, setDateRange] = useState([
        moment().subtract(4, 'days').startOf('day'),
        moment().endOf('day')
    ]);

    // Modal
    const [selectedRow, setSelectedRow] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    // ---------- Helpers ----------
    const formatDateForAPI = (date) => (date ? moment(date).format('YYYY-MM-DD') : null);

    const buildParams = () => {
        const params = {
            page,
            limit: isLimit,
            onlyDuplicates: onlyDuplicates ? 'true' : 'false',
        };

        if (partnerId.trim()) params.partnerId = partnerId.trim();

        // Dates mandatory
        if (dateRange && dateRange[0]) {
            params.from = formatDateForAPI(dateRange[0]);
        } else {
            params.from = moment().subtract(4, 'days').format('YYYY-MM-DD');
        }
        if (dateRange && dateRange[1]) {
            params.to = formatDateForAPI(dateRange[1]);
        } else {
            params.to = moment().format('YYYY-MM-DD');
        }

        return params;
    };

    const objectToQueryString = (obj) => {
        const str = [];
        for (const p in obj) {
            if (obj.hasOwnProperty(p) && obj[p] !== undefined && obj[p] !== null && obj[p] !== "") {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        }
        return str.join("&");
    };

    // ---------- Fetch ----------
    const getAuditData = (overridePage) => {
        setLoading(true);

        const currentPage = overridePage || page;
        const queryParams = { ...buildParams(), page: currentPage };
        const queryString = objectToQueryString(queryParams);

        getRequest(`${DMT_REFUND_AUDIT_LIST}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    setData(response.data || []);
                    setSummary(response.summary || null);
                    setPagination(response.pagination || { page: currentPage, total: 0, totalPages: 1 });
                    setPage(response.pagination?.page || currentPage);
                } else {
                    setData([]);
                    setSummary(null);
                    setPagination({ page: 1, total: 0, totalPages: 1 });
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log("Audit fetch error:", error);
                setLoading(false);
                toast.error("Failed to load audit data");
            });
    };

    // ---------- CSV Export (matches your manual export pattern) ----------
    const handleManualExport = () => {
        if (!getData || getData.length === 0) {
            toast.info("No data to export");
            return;
        }

        const headers = [
            'Transaction ID',
            'Partner ID',
            'Original Amount',
            'Total Refunded',
            'Excess Amount',
            'Refund Count',
            'Status',
            'First Refund At',
            'Last Refund At',
        ];

        const csvRows = [headers.join(',')];

        getData.forEach(item => {
            const refunds = item.refunds || [];
            const firstAt = refunds[0]?.createdAt || '';
            const lastAt = refunds[refunds.length - 1]?.createdAt || '';

            const row = [
                `"${String(item.transactionId || '').replace(/"/g, '""')}"`,
                `"${String(item.partnerId || '').replace(/"/g, '""')}"`,
                item.originalAmount || 0,
                item.totalRefunded || 0,
                item.excessAmount || 0,
                item.refundCount || 0,
                item.status || '',
                firstAt ? moment(firstAt).format('DD/MM/YYYY HH:mm:ss') : '',
                lastAt ? moment(lastAt).format('DD/MM/YYYY HH:mm:ss') : '',
            ];
            csvRows.push(row.join(','));
        });

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `dmt-refund-audit-${moment().format('DD-MM-YYYY-HH-mm-ss')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success(`Exported ${getData.length} records successfully`);
    };

    // ---------- Handlers ----------
    const handleSearch = () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            toast.error("Please select a valid date range.");
            return;
        }
        setPage(1);
        getAuditData(1);
    };

    const handleClear = () => {
        setPartnerId("");
        setOnlyDuplicates(true);
        setDateRange([
            moment().subtract(4, 'days').startOf('day'),
            moment().endOf('day')
        ]);
        setPage(1);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        getAuditData(newPage);
    };

    const handleRowsPerPageChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
        // re-fetch with new limit
        setTimeout(() => getAuditData(1), 0);
    };

    const handleViewDetails = (row) => {
        setSelectedRow(row);
        setIsModalVisible(true);
    };

    // ---------- Table columns ----------
    const columns = [
        {
            name: 'Transaction ID',
            selector: row => row.transactionId,
            cell: row => (
                <Tooltip title="Click to view refund history">
                    <span
                        className="text-blue-600 cursor-pointer hover:underline"
                        onClick={() => handleViewDetails(row)}
                        style={{
                            fontFamily: "'Courier New', monospace",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block"
                        }}
                    >
                        {row.transactionId}
                    </span>
                </Tooltip>
            ),
            width: "220px",
            minWidth: "200px",
            maxWidth: "240px",
            sortable: true,
        },
        {
            name: 'Partner',
            selector: row => row.partnerId,
            cell: row => (
                <Tooltip title={row.partnerId}>
                    <Tag color="blue" style={{
                        fontSize: "12px",
                        padding: "2px 8px",
                        whiteSpace: "nowrap"
                    }}>
                        {row.partnerId ? String(row.partnerId).slice(-8) : '-'}
                    </Tag>
                </Tooltip>
            ),
            width: "120px",
            minWidth: "100px",
            maxWidth: "140px",
            sortable: true,
        },
        {
            name: 'Original',
            selector: row => row.originalAmount,
            cell: row => (
                <span style={{ fontSize: "12px", fontWeight: 500 }}>
                    ₹{Number(row.originalAmount || 0).toLocaleString('en-IN')}
                </span>
            ),
            width: "110px",
            minWidth: "100px",
            maxWidth: "120px",
            right: true,
            sortable: true,
        },
        {
            name: 'Total Refunded',
            selector: row => row.totalRefunded,
            cell: row => (
                <span style={{ fontSize: "12px", fontWeight: 500 }}>
                    ₹{Number(row.totalRefunded || 0).toLocaleString('en-IN')}
                </span>
            ),
            width: "130px",
            minWidth: "110px",
            maxWidth: "140px",
            right: true,
            sortable: true,
        },
        {
            name: 'Excess',
            selector: row => row.excessAmount,
            cell: row => (
                <span style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: row.excessAmount > 0 ? "crimson" : "#4a5568"
                }}>
                    ₹{Number(row.excessAmount || 0).toLocaleString('en-IN')}
                </span>
            ),
            width: "110px",
            minWidth: "100px",
            maxWidth: "120px",
            right: true,
            sortable: true,
        },
        {
            name: 'Refunds',
            selector: row => row.refundCount,
            cell: row => (
                <Badge
                    count={row.refundCount}
                    showZero
                    style={{
                        backgroundColor: row.refundCount > 1 ? '#faad14' : '#52c41a',
                    }}
                />
            ),
            width: "90px",
            minWidth: "80px",
            maxWidth: "100px",
            center: true,
            sortable: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            cell: row => {
                const colorMap = {
                    DUPLICATE_REFUND: 'red',
                    OVER_REFUND: 'red',
                    DUPLICATE_AND_OVER_REFUND: 'red',
                    FULL_REFUND: 'green',
                    PARTIAL_REFUND: 'orange',
                    NO_REFUND: 'default',
                };
                return (
                    <Tag color={colorMap[row.status] || 'default'} style={{ fontSize: "11px", padding: "2px 8px" }}>
                        {row.status}
                    </Tag>
                );
            },
            width: "180px",
            minWidth: "160px",
            maxWidth: "200px",
            sortable: true,
        },
        {
            name: '',
            selector: row => row.transactionId,
            cell: row => (
                <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => handleViewDetails(row)}
                    style={{ color: "#1890ff", minWidth: "40px" }}
                />
            ),
            width: "70px",
            minWidth: "60px",
            maxWidth: "80px",
            center: true,
        },
    ];

    // Initial fetch
    useEffect(() => {
        getAuditData(1);
        // eslint-disable-next-line
    }, []);

    // ---------- Render ----------
    return (
        <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "20px" }}>
            <div style={{ maxWidth: "1400px", margin: "0 auto" }}>

                {/* Header */}
                <div style={{ marginBottom: "24px" }}>
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px"
                    }}>
                        <div>
                            <h1 style={{
                                margin: 0,
                                fontSize: "24px",
                                fontWeight: "600",
                                color: "#1a202c"
                            }}>
                                DMT Refund Audit
                            </h1>
                            <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#718096" }}>
                                Read-only audit — no records are modified
                            </p>
                        </div>

                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleManualExport}
                                type="primary"
                                loading={isLoading}
                                disabled={!getData.length}
                            >
                                Export
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <Card style={{
                    marginBottom: "24px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0"
                }}>
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <FilterOutlined style={{ color: "#4a5568" }} />
                            <span style={{ fontWeight: "500", color: "#4a5568" }}>Filters</span>
                        </div>

                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button icon={<ReloadOutlined />} onClick={handleClear}>
                                Clear
                            </Button>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                            >
                                Audit
                            </Button>
                        </div>
                    </div>

                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: "16px"
                    }}>
                        <div>
                            <label style={labelStyle}>
                                Date Range <span style={{ color: "red" }}>*</span>
                            </label>
                            <RangePicker
                                style={{ width: "100%" }}
                                format="DD/MM/YYYY"
                                value={dateRange}
                                onChange={(dates) => setDateRange(
                                    dates || [moment().subtract(4, 'days').startOf('day'), moment().endOf('day')]
                                )}
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Partner ID</label>
                            <Input
                                placeholder="Enter Partner Id"
                                value={partnerId}
                                onChange={(e) => setPartnerId(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Only Duplicate Refunds</label>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                height: 32
                            }}>
                                <input
                                    type="checkbox"
                                    checked={onlyDuplicates}
                                    onChange={(e) => setOnlyDuplicates(e.target.checked)}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: 13, color: '#4a5568' }}>
                                    {onlyDuplicates ? 'Showing duplicates only' : 'Showing all audit results'}
                                </span>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Summary Cards */}
                {getSummary && (
                    <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
                        <Col xs={12} sm={12} md={8} lg={4}>
                            <SummaryCard
                                label="Transactions Audited"
                                value={getSummary.transactionsAudited}
                            />
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={4}>
                            <SummaryCard
                                label="With Refund"
                                value={getSummary.transactionsWithRefund}
                            />
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={4}>
                            <SummaryCard
                                label="Partial Refunds"
                                value={getSummary.partialRefunds}
                                color="#faad14"
                            />
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={4}>
                            <SummaryCard
                                label="Full Refunds"
                                value={getSummary.fullRefunds}
                                color="#52c41a"
                            />
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={4}>
                            <SummaryCard
                                label="Duplicate Txns"
                                value={getSummary.duplicateTransactions}
                                color="crimson"
                                highlight
                            />
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={4}>
                            <SummaryCard
                                label="Excess Refund"
                                value={`₹${Number(getSummary.totalExcessRefund || 0).toLocaleString('en-IN')}`}
                                color="crimson"
                                highlight
                            />
                        </Col>
                    </Row>
                )}

                {/* Data Table */}
                <Card style={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden"
                }}>
                    <div style={{
                        padding: "16px",
                        borderBottom: "1px solid #e2e8f0",
                        background: "#f8f9fa"
                    }}>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600" }}>
                                    Refund Records
                                </h3>
                                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#718096" }}>
                                    {getData.length} of {getPagination.total || 0} records
                                </p>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <span style={{ fontSize: "13px", color: "#718096" }}>Show:</span>
                                <select
                                    style={{
                                        padding: "6px 12px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "4px",
                                        fontSize: "13px"
                                    }}
                                    value={isLimit}
                                    onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                                >
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <DataTable
                        columns={columns}
                        data={getData}
                        progressPending={isLoading}
                        pagination
                        paginationServer
                        paginationTotalRows={getPagination.total || 0}
                        paginationRowsPerPageOptions={[20, 50, 100, 200]}
                        paginationPerPage={isLimit}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange}
                        highlightOnHover
                        fixedHeader
                        fixedHeaderScrollHeight="calc(100vh - 500px)"
                        dense
                        noDataComponent={
                            <div style={{
                                padding: "48px 0",
                                textAlign: "center",
                                color: "#718096"
                            }}>
                                <div style={{ fontSize: "16px", marginBottom: "8px" }}>
                                    No {onlyDuplicates ? 'duplicate refunds' : 'records'} found
                                </div>
                                <p style={{ fontSize: "14px" }}>
                                    Try adjusting the date range or clearing filters
                                </p>
                            </div>
                        }
                        customStyles={customTableStyles}
                    />
                </Card>

                {/* Modal — Refund History */}
                <Modal
                    title="Refund Audit Details"
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setIsModalVisible(false)}>
                            Close
                        </Button>
                    ]}
                    width={720}
                >
                    {selectedRow && <RefundDetailBody row={selectedRow} />}
                </Modal>
            </div>
        </div>
    );
};

// ---------- Summary Card ----------
const SummaryCard = ({ label, value, color = "#1a202c", highlight = false }) => (
    <Card
        style={{
            borderRadius: 8,
            border: `1px solid ${highlight ? "#f3c2c2" : "#e2e8f0"}`,
            background: highlight ? "#fff5f5" : "#ffffff",
        }}
        bodyStyle={{ padding: 16 }}
    >
        <div style={{ fontSize: 12, color: "#718096", marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
    </Card>
);

// ---------- Modal Body ----------
const RefundDetailBody = ({ row }) => {
    const refunds = row.refunds || [];

    return (
        <div style={{ padding: "8px 0" }}>
            <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "20px"
            }}>
                <InfoBlock label="Transaction ID" value={row.transactionId} mono />
                <InfoBlock label="Partner ID" value={row.partnerId || '-'} tag />
                <InfoBlock label="Original Amount" value={`₹${Number(row.originalAmount || 0).toLocaleString('en-IN')}`} />
                <InfoBlock label="Total Refunded" value={`₹${Number(row.totalRefunded || 0).toLocaleString('en-IN')}`} />
                <InfoBlock
                    label="Excess"
                    value={`₹${Number(row.excessAmount || 0).toLocaleString('en-IN')}`}
                    color={row.excessAmount > 0 ? "crimson" : "#1a202c"}
                    bold
                />
                <InfoBlock
                    label="Status"
                    value={row.status}
                    color={row.status === 'DUPLICATE_REFUND' || row.status === 'OVER_REFUND' ? 'crimson' : '#1a202c'}
                    bold
                />
            </div>

            <div style={{ fontSize: "13px", color: "#718096", marginBottom: "8px" }}>
                Refund History (chronological)
            </div>

            <div style={{
                maxHeight: 380,
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                            <th style={thStyle}>#</th>
                            <th style={thStyle}>Amount</th>
                            <th style={thStyle}>Running Total</th>
                            <th style={thStyle}>Time</th>
                            <th style={thStyle}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {refunds.map((r, idx) => (
                            <tr key={r.ledgerId || idx}>
                                <td style={tdStyle}>{idx + 1}</td>
                                <td style={tdStyle}>
                                    ₹{Number(r.amount || 0).toLocaleString('en-IN')}
                                </td>
                                <td style={tdStyle}>
                                    ₹{Number(r.runningTotal || 0).toLocaleString('en-IN')}
                                </td>
                                <td style={{ ...tdStyle, fontSize: 12 }}>
                                    {r.createdAt ? moment(r.createdAt).format('DD/MM/YY HH:mm:ss.SSS') : '-'}
                                </td>
                                <td style={tdStyle}>
                                    {r.status === 'VALID' ? (
                                        <Tag color="green">VALID</Tag>
                                    ) : (
                                        <Tooltip title={r.reason || ''}>
                                            <Tag color="red">DUPLICATE</Tag>
                                        </Tooltip>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {refunds.length === 0 && (
                            <tr>
                                <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>
                                    No refund records
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const InfoBlock = ({ label, value, mono, tag, color, bold }) => (
    <div>
        <div style={{ fontSize: "13px", color: "#718096", marginBottom: "4px" }}>{label}</div>
        {tag ? (
            <Tag color="blue" style={{ wordBreak: 'break-all' }}>{value}</Tag>
        ) : (
            <div style={{
                fontFamily: mono ? "'Courier New', monospace" : 'inherit',
                fontSize: "14px",
                fontWeight: bold ? 600 : 500,
                color: color || "#1a202c",
                wordBreak: "break-all",
            }}>
                {value}
            </div>
        )}
    </div>
);

// ---------- Local styles ----------
const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontSize: "13px",
    color: "#4a5568"
};

const thStyle = {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: 12,
    fontWeight: 600,
    color: '#4a5568',
    borderBottom: '1px solid #e2e8f0',
};

const tdStyle = {
    padding: '8px 10px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 13,
    color: '#4a5568',
};

const customTableStyles = {
    table: { style: { tableLayout: 'fixed' } },
    headCells: {
        style: {
            backgroundColor: '#f8f9fa',
            padding: '12px 16px',
            fontSize: '13px',
            fontWeight: 600,
            color: '#4a5568',
            borderBottom: '1px solid #e2e8f0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    },
    cells: {
        style: {
            padding: '12px 16px',
            fontSize: '12px',
            color: '#4a5568',
            borderBottom: '1px solid #f1f5f9',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    },
    rows: {
        style: {
            '&:hover': { backgroundColor: '#f8fafc' },
        },
    },
};

export default DmtRefundAuditPage;